import type { CSSObject } from '@emotion/react'
import { MapItemsCache } from 'components/GoogleMap/MapItemsCache'
import type { ReactComponentOverlayView } from 'components/GoogleMap/_utils/makeReactComponentOverlayView'
import { makeReactComponentOverlayView } from 'components/GoogleMap/_utils/makeReactComponentOverlayView'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import type { TBlockId, TPropertyId } from 'stores/fieldAssetStore/helperTypes'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getPrimaryValueGroup } from 'stores/selectedValueGroupsStore/getPrimaryValueGroup'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { shouldShowNDVI } from 'utils/shouldShowNDVI'
import { getZoomThresholds } from '../../_utils/zoomVisibility'
import { BlockValueBadgeComponent } from './BlockValueBadgeComponent/BlockValueBadgeComponent'

export type BLOCK_VALUE_META = {
  blockId: TBlockId
  blockName: string
  bounds: google.maps.LatLngBounds
  latLngs: google.maps.LatLng[]
  latLng: google.maps.LatLng
  propertyId: TPropertyId
}

// TODO: will eventually come from current values type
export type TBlocksValueTypesToPropsDictionary = Record<
  string,
  { children: React.ReactNode; getContainerCSS: () => CSSObject }
>

export class BlockValuesCache extends MapItemsCache<
  BLOCK_VALUE_META,
  TBlocksValueTypesToPropsDictionary,
  {
    badge: ReactComponentOverlayView<{
      meta: BLOCK_VALUE_META
      valueTypesToPropsDictionary: TBlocksValueTypesToPropsDictionary
    }>
    polygon?: google.maps.Polygon
  }
> {
  constructor(options: { maps: typeof google.maps; map: google.maps.Map }) {
    super({
      createOverlay: (o) => {
        const polygon = hubConnectorSettings.enableBlockPolygons
          ? new o.maps.Polygon({ paths: o.meta.latLngs, map: o.map })
          : undefined

        // TODO: use util for new block select
        polygon?.addListener('click', () => {
          setSelectedFieldAsset({ block: o.meta.blockId })
        })

        return {
          polygon,
          badge: makeReactComponentOverlayView({
            component: BlockValueBadgeComponent,
            latLng: o.meta.latLng,
            maps: o.maps,
            map: o.map,
          }),
        }
      },
      renderOverlay: (o) => {
        const isSelected = selectedFieldAssetsStore.getState().block === o.meta.blockId
        const primaryValueGroup = getPrimaryValueGroup()
        const { mapVisualValueGroup } = mapControlsStore.getState()

        const valueType = !primaryValueGroup
          ? null
          : mapVisualValueGroup[MAP_VISUAL.BLOCK]?.[primaryValueGroup]?.valueType

        const mapZoom = o.map.getZoom()
        const ndviIsVisible = shouldShowNDVI() && mapZoom && getZoomThresholds(mapZoom).BLOCK === 'OK'

        o.overlay.polygon?.setOptions({
          map: o.map,
          strokeColor: isSelected ? 'yellow' : 'white',
          strokeWeight: isSelected ? 4 : 2,
          strokePosition: o.maps.StrokePosition.CENTER,
          fillColor: valueType
            ? String(o.value?.[valueType]?.getContainerCSS?.()?.['backgroundColor'])
            : null,
          fillOpacity: ndviIsVisible ? 0.01 : 0.6,
        })

        o.overlay.badge.render({ ...o, valueTypesToPropsDictionary: o.value })
      },
      hideOverlay: (o) => {
        o.overlay.polygon?.setMap(null)

        o.overlay.badge.setMap(null)
      },
      maps: options.maps,
      map: options.map,
    })
  }
}
